import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableSortLabel,
  TableContainer,
  LinearProgress,
  TablePagination
} from '@material-ui/core';
import { AppSnackbar } from '@sierralabs/react-material-ui';

import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import {
  useQueryParam,
  NumberParam,
  StringParam,
  withDefault
} from 'use-query-params';
import {
  useProductEntityPrefix,
  useFindProductDesignInputs
} from '../Product.hooks';
import {
  BaseProductEntityTableProps,
  ProductComponent,
  ProductDesignInput
} from '../Product.interface';
import { getProductEntityName } from '../Product.config';
import PageTemplateCardOver from '../../../common/page-template/PageTemplateCardOver';
import { stripHtml } from '../../../common/formatter';

const DesignInputsTable: React.FC<BaseProductEntityTableProps> = (
  props: BaseProductEntityTableProps
) => {
  const { productId, apiParams, fetchRef } = props;
  const history = useHistory();
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [direction, setDirection] = useQueryParam('direction', StringParam);

  const {
    productDesignInputList,
    isLoadingProductDesignInputs,
    loadingProductDesignInputsError,
    findProductDesignInputs
  } = useFindProductDesignInputs(productId);

  const findProductDesignInputsWithParams = useCallback(() => {
    findProductDesignInputs({ page, limit, order, direction, ...apiParams });
  }, [findProductDesignInputs, page, limit, order, direction, apiParams]);

  useEffect(() => {
    if (fetchRef) {
      fetchRef.current = findProductDesignInputsWithParams;
    }
    findProductDesignInputsWithParams();
  }, [fetchRef, findProductDesignInputsWithParams]);

  const prefix: string = useProductEntityPrefix(
    ProductBoardColumnEntity.DesignInput
  );

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };
  console.log('productDesignInputList', productDesignInputList);
  return (
    <PageTemplateCardOver>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'productDesignInput.id'}
                  direction={getSortDirection('productDesignInput.id')}
                  onClick={() => handleSort('productDesignInput.id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productDesignInput.title'}
                  direction={getSortDirection('productDesignInput.title')}
                  onClick={() => handleSort('productDesignInput.title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productDesignInput.description'}
                  direction={getSortDirection('productDesignInput.description')}
                  onClick={() => handleSort('productDesignInput.description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'productDesignInput.createdBy'}
                  direction={getSortDirection('productDesignInput.createdBy')}
                  onClick={() => handleSort('productDesignInput.createdBy')}
                >
                  Author
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.user'}
                  direction={getSortDirection('task.user')}
                  onClick={() => handleSort('task.user')}
                >
                  Assignee
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'components.name'}
                  direction={getSortDirection('components.name')}
                  onClick={() => handleSort('components.name')}
                >
                  Component
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'release.name'}
                  direction={getSortDirection('release.name')}
                  onClick={() => handleSort('release.name')}
                >
                  Release
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingProductDesignInputs && (
              <TableRow>
                <TableCell colSpan={7} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {productDesignInputList?.data?.map(
              (designInput: ProductDesignInput) => (
                <TableRow
                  hover
                  key={designInput.id}
                  onClick={() =>
                    history.push(
                      `/portal/products/${productId}/design-inputs/${designInput.id}`
                    )
                  }
                >
                  <TableCell>{prefix + designInput.id}</TableCell>
                  <WrappedTableCell>{designInput.title}</WrappedTableCell>
                  <WrappedTableCell>
                    <div className='truncate'>
                      {stripHtml(designInput.description || '')}
                    </div>
                  </WrappedTableCell>
                  <TableCell>
                    {`${designInput.task.createdBy?.firstName} ${designInput.task.createdBy?.lastName}`}
                  </TableCell>
                  <TableCell>
                    {designInput.task.user
                      ? `${designInput.task.user?.firstName} ${designInput.task.user.lastName}`
                      : 'No User Assigned'}
                  </TableCell>
                  <TableCell>
                    <TaskStatusLabel task={designInput.task} />
                  </TableCell>
                  <TableCell>
                    {designInput.components.length !== 0
                      ? designInput.components
                          .map((component: ProductComponent) => component.name)
                          .join(', ')
                      : 'No Components'}
                  </TableCell>
                  <TableCell>
                    {designInput.release
                      ? designInput.release.name
                      : 'No Release'}
                  </TableCell>
                </TableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component='div'
        count={productDesignInputList?.total || 0}
        rowsPerPage={limit}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={event => {
          setLimit(+event.target.value);
          setPage(0);
        }}
      />
      <AppSnackbar
        severity='error'
        open={Boolean(loadingProductDesignInputsError)}
      >
        There was an error loading the list of{' '}
        {getProductEntityName('designInput')}.
      </AppSnackbar>
    </PageTemplateCardOver>
  );
};

export default DesignInputsTable;
